import {
    Check,
    CheckCircle2,
    Eye,
    EyeOff,
    KeyRound,
    LoaderCircle,
    LockKeyhole,
    Mail,
    MessageSquareText,
    Phone,
    ShieldCheck,
    X,
    XCircle,
} from "lucide-react";
import {
    FormEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAuth } from "../../../../context/AuthContext";
import {
    changeTransactionPassword,
    clearState,
} from "../../../../Redux/custmoer_auth/slice";
import { AppDispatch } from "../../../../store";

import "./ChangeTransactionPasswordPage.css";

type PasswordVisibility = {
    newTransactionPassword: boolean;
    confirmTransactionPassword: boolean;
};

type OtpStatus =
    | "idle"
    | "sending"
    | "sent"
    | "verifying"
    | "verified"
    | "invalid"
    | "error";

const SPECIAL_CHARACTER_PATTERN =
    /[#?!@$%^&*-]/;

const getRequestError = (
    error: unknown,
    fallback: string
) =>
    typeof error === "string"
        ? error
        : error instanceof Error
            ? error.message
            : fallback;

const ChangeTransactionPasswordPage =
    () => {
        const dispatch =
            useDispatch<AppDispatch>();
        const navigate =
            useNavigate();
        const { user } =
            useAuth();
        const otpRequestIdRef =
            useRef(0);

        const [
            mobileNumber,
            setMobileNumber,
        ] =
            useState("");

        const [
            email,
            setEmail,
        ] =
            useState("");

        const [
            otp,
            setOtp,
        ] =
            useState("");

        const [
            newTransactionPassword,
            setNewTransactionPassword,
        ] =
            useState("");

        const [
            confirmTransactionPassword,
            setConfirmTransactionPassword,
        ] =
            useState("");

        const [
            visibility,
            setVisibility,
        ] =
            useState<PasswordVisibility>(
                {
                    newTransactionPassword:
                        false,
                    confirmTransactionPassword:
                        false,
                }
            );

        const [
            otpStatus,
            setOtpStatus,
        ] =
            useState<OtpStatus>(
                "idle"
            );

        const [
            otpMessage,
            setOtpMessage,
        ] =
            useState("");

        const [
            resendSeconds,
            setResendSeconds,
        ] =
            useState(0);

        const [
            submitting,
            setSubmitting,
        ] =
            useState(false);

        const [
            success,
            setSuccess,
        ] =
            useState(false);

        const [
            submitError,
            setSubmitError,
        ] =
            useState("");

        useEffect(
            () => {
                if (
                    !user
                ) {
                    return;
                }

                setEmail(
                    user.emailId ||
                    user.email ||
                    ""
                );

                setMobileNumber(
                    (user.phone || "")
                        .replace(
                            /\D/g,
                            ""
                        )
                        .slice(
                            -10
                        )
                );
            },
            [user]
        );

        useEffect(
            () => () => {
                dispatch(
                    clearState()
                );
            },
            [dispatch]
        );

        useEffect(
            () => {
                if (
                    resendSeconds <=
                    0
                ) {
                    return;
                }

                const timer =
                    window.setInterval(
                        () => {
                            setResendSeconds(
                                (
                                    previous
                                ) =>
                                    Math.max(
                                        0,
                                        previous -
                                        1
                                    )
                            );
                        },
                        1000
                    );

                return () =>
                    window.clearInterval(
                        timer
                    );
            },
            [
                resendSeconds,
            ]
        );

        const passwordRules =
            useMemo(
                () => [
                    {
                        id:
                            "length",
                        label:
                            "At least 8 characters",
                        valid:
                            newTransactionPassword.length >=
                            8 &&
                            newTransactionPassword.length <=
                            15,
                    },
                    {
                        id:
                            "uppercase",
                        label:
                            "At least one uppercase letter",
                        valid:
                            /[A-Z]/.test(
                                newTransactionPassword
                            ),
                    },
                    {
                        id:
                            "lowercase",
                        label:
                            "At least one lowercase letter",
                        valid:
                            /[a-z]/.test(
                                newTransactionPassword
                            ),
                    },
                    {
                        id:
                            "number",
                        label:
                            "At least one number",
                        valid:
                            /\d/.test(
                                newTransactionPassword
                            ),
                    },
                    {
                        id:
                            "special",
                        label:
                            "At least one special character",
                        valid:
                            SPECIAL_CHARACTER_PATTERN.test(
                                newTransactionPassword
                            ),
                    },
                ],
                [
                    newTransactionPassword,
                ]
            );

        const allPasswordRulesValid =
            passwordRules.every(
                (
                    rule
                ) =>
                    rule.valid
            );

        const passwordMatchStatus =
            !confirmTransactionPassword
                ? "idle"
                : newTransactionPassword ===
                    confirmTransactionPassword
                    ? "matched"
                    : "not-matched";

        const validMobile =
            /^[0-9]{10}$/.test(
                mobileNumber
            );

        const validEmail =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                email
            );

        const canRequestOtp =
            validMobile &&
            validEmail &&
            otpStatus !==
            "sending" &&
            resendSeconds ===
            0;

        const canVerifyOtp =
            /^\d{6}$/.test(
                otp
            ) &&
            otpStatus !==
            "verifying" &&
            otpStatus !==
            "verified";

        const canSubmit =
            otpStatus ===
            "verified" &&
            allPasswordRulesValid &&
            passwordMatchStatus ===
            "matched" &&
            !submitting;

        const toggleVisibility =
            (
                field: keyof PasswordVisibility
            ) => {
                setVisibility(
                    (
                        previous
                    ) => ({
                        ...previous,
                        [field]:
                            !previous[
                            field
                            ],
                    })
                );
            };

        const resetOtpVerification =
            () => {
                otpRequestIdRef.current +=
                    1;

                if (
                    otpStatus !==
                    "idle"
                ) {
                    setOtpStatus(
                        "idle"
                    );

                    setOtp(
                        ""
                    );

                    setOtpMessage(
                        ""
                    );

                    setResendSeconds(
                        0
                    );
                }
            };

        const handleMobileChange =
            (
                value: string
            ) => {
                setMobileNumber(
                    value
                        .replace(
                            /\D/g,
                            ""
                        )
                        .slice(
                            0,
                            10
                        )
                );

                resetOtpVerification();
                setSuccess(
                    false
                );
            };

        const handleEmailChange =
            (
                value: string
            ) => {
                setEmail(
                    value
                );

                resetOtpVerification();
                setSuccess(
                    false
                );
            };

        const handleRequestOtp =
            async () => {
                if (
                    !canRequestOtp
                ) {
                    return;
                }

                setOtpStatus(
                    "sending"
                );

                setOtpMessage(
                    ""
                );

                setSubmitError(
                    ""
                );

                const requestId =
                    ++otpRequestIdRef.current;

                try {
                    const response =
                        await dispatch(
                            changeTransactionPassword(
                                {
                                    mobileNumber,
                                    emailId:
                                        email.trim(),
                                }
                            )
                        ).unwrap();

                    if (
                        otpRequestIdRef.current !==
                        requestId
                    ) {
                        return;
                    }

                    setOtpStatus(
                        "sent"
                    );

                    setOtp(
                        ""
                    );

                    setOtpMessage(
                        response.message ||
                        "OTP sent successfully."
                    );

                    setResendSeconds(
                        60
                    );
                } catch (
                error
                ) {
                    if (
                        otpRequestIdRef.current !==
                        requestId
                    ) {
                        return;
                    }

                    setOtpStatus(
                        "error"
                    );

                    setOtpMessage(
                        getRequestError(
                            error,
                            "Unable to send OTP."
                        )
                    );
                } finally {
                    dispatch(
                        clearState()
                    );
                }
            };

        const handleVerifyOtp =
            () => {
                if (
                    !canVerifyOtp
                ) {
                    return;
                }

                setOtpStatus(
                    "verified"
                );

                setOtpMessage(
                    "OTP is ready and will be verified when you update."
                );
            };

        const handleSubmit =
            async (
                event: FormEvent<HTMLFormElement>
            ) => {
                event.preventDefault();

                if (
                    !canSubmit
                ) {
                    return;
                }

                setSubmitting(
                    true
                );

                setSubmitError(
                    ""
                );

                setSuccess(
                    false
                );

                try {
                    const response =
                        await dispatch(
                            changeTransactionPassword(
                                {
                                    mobileNumber,
                                    emailId:
                                        email.trim(),
                                    otp,
                                    newTransactionPassword,
                                }
                            )
                        ).unwrap();

                    setSuccess(
                        true
                    );

                    setOtpStatus(
                        "idle"
                    );

                    setOtpMessage(
                        ""
                    );

                    setOtp(
                        ""
                    );

                    setNewTransactionPassword(
                        ""
                    );

                    setConfirmTransactionPassword(
                        ""
                    );

                    setResendSeconds(
                        0
                    );

                    toast.success(
                        response.message ||
                        "Transaction password updated successfully."
                    );

                    navigate(
                        "/Dashboard"
                    );
                } catch (
                error
                ) {
                    const message =
                        getRequestError(
                            error,
                            "Unable to update transaction password."
                        );

                    if (
                        message.toLowerCase().includes(
                            "otp"
                        )
                    ) {
                        setOtpStatus(
                            "invalid"
                        );

                        setOtpMessage(
                            message
                        );
                    } else {
                        setSubmitError(
                            message
                        );
                    }
                } finally {
                    setSubmitting(
                        false
                    );

                    dispatch(
                        clearState()
                    );
                }
            };

        return (
            <section className="change-txn-password-page">
                <div className="change-txn-password-shell">
                    <div className="change-txn-password-heading">
                        <span className="change-txn-password-kicker">
                            MY ACCOUNT
                        </span>

                        <h1>
                            Change Transaction Password
                        </h1>

                        <p>
                            Verify your registered contact details using OTP and create a secure new transaction password.
                        </p>
                    </div>

                    {success && (
                        <div className="change-txn-password-message success">
                            <CheckCircle2
                                size={
                                    17
                                }
                            />

                            <span>
                                Transaction password updated successfully.
                            </span>
                        </div>
                    )}

                    {submitError && (
                        <div className="change-txn-password-message error">
                            <XCircle
                                size={
                                    17
                                }
                            />

                            <span>
                                {
                                    submitError
                                }
                            </span>
                        </div>
                    )}

                    <form
                        className="change-txn-password-form"
                        onSubmit={
                            handleSubmit
                        }
                    >
                        <section className="change-txn-password-card">
                            <div className="change-txn-password-card-head">
                                <span className="change-txn-password-card-icon">
                                    <ShieldCheck
                                        size={
                                            19
                                        }
                                        strokeWidth={
                                            1.9
                                        }
                                    />
                                </span>

                                <div>
                                    <strong>
                                        Verify your account
                                    </strong>

                                    <small>
                                        We will verify your registered mobile number and e-mail before allowing this change.
                                    </small>
                                </div>
                            </div>

                            <div className="change-txn-password-field">
                                <label htmlFor="txn-mobile-number">
                                    Mobile Number
                                </label>

                                <div className="change-txn-password-input-wrap">
                                    <Phone
                                        size={
                                            17
                                        }
                                        strokeWidth={
                                            1.8
                                        }
                                    />

                                    <input
                                        id="txn-mobile-number"
                                        type="tel"
                                        inputMode="numeric"
                                        value={
                                            mobileNumber
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleMobileChange(
                                                event.target.value
                                            )
                                        }
                                        placeholder="Enter your mobile number"
                                    />
                                </div>

                                {mobileNumber &&
                                    !validMobile && (
                                        <div className="change-txn-password-field-status invalid">
                                            <XCircle
                                                size={
                                                    14
                                                }
                                            />

                                            <span>
                                                Enter a valid 10 digit mobile number.
                                            </span>
                                        </div>
                                    )}
                            </div>

                            <div className="change-txn-password-field">
                                <label htmlFor="txn-email">
                                    E-mail ID
                                </label>

                                <div className="change-txn-password-input-wrap">
                                    <Mail
                                        size={
                                            17
                                        }
                                        strokeWidth={
                                            1.8
                                        }
                                    />

                                    <input
                                        id="txn-email"
                                        type="email"
                                        value={
                                            email
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            handleEmailChange(
                                                event.target.value
                                            )
                                        }
                                        placeholder="Enter your e-mail ID"
                                    />
                                </div>

                                {email &&
                                    !validEmail && (
                                        <div className="change-txn-password-field-status invalid">
                                            <XCircle
                                                size={
                                                    14
                                                }
                                            />

                                            <span>
                                                Enter a valid e-mail address.
                                            </span>
                                        </div>
                                    )}
                            </div>

                            <button
                                type="button"
                                className="change-txn-password-otp-button"
                                onClick={
                                    handleRequestOtp
                                }
                                disabled={
                                    !canRequestOtp
                                }
                            >
                                {otpStatus ===
                                    "sending" ? (
                                    <>
                                        <LoaderCircle
                                            size={
                                                15
                                            }
                                            className="spin"
                                        />

                                        Sending OTP...
                                    </>
                                ) : resendSeconds >
                                    0 ? (
                                    `Resend OTP in ${resendSeconds}s`
                                ) : (
                                    <>
                                        <MessageSquareText
                                            size={
                                                15
                                            }
                                        />

                                        Get OTP
                                    </>
                                )}
                            </button>

                            {(otpStatus ===
                                "sent" ||
                                otpStatus ===
                                "verifying" ||
                                otpStatus ===
                                "verified" ||
                                otpStatus ===
                                "invalid" ||
                                otpStatus ===
                                "error") && (
                                    <div className="change-txn-password-field">
                                        <label htmlFor="txn-otp">
                                            OTP
                                        </label>

                                        <div className="change-txn-password-input-wrap">
                                            <MessageSquareText
                                                size={
                                                    17
                                                }
                                                strokeWidth={
                                                    1.8
                                                }
                                            />

                                            <input
                                                id="txn-otp"
                                                type="text"
                                                inputMode="numeric"
                                                value={
                                                    otp
                                                }
                                                maxLength={
                                                    6
                                                }
                                                disabled={
                                                    otpStatus ===
                                                    "verified"
                                                }
                                                onChange={(
                                                    event
                                                ) => {
                                                    setOtp(
                                                        event.target.value
                                                            .replace(
                                                                /\D/g,
                                                                ""
                                                            )
                                                            .slice(
                                                                0,
                                                                6
                                                            )
                                                    );

                                                    if (
                                                        otpStatus ===
                                                        "invalid"
                                                    ) {
                                                        setOtpStatus(
                                                            "sent"
                                                        );

                                                        setOtpMessage(
                                                            ""
                                                        );
                                                    }
                                                }}
                                                placeholder="Enter OTP"
                                            />

                                            <button
                                                type="button"
                                                className={`change-txn-password-verify-otp ${otpStatus ===
                                                    "verified"
                                                    ? "verified"
                                                    : ""
                                                    }`}
                                                onClick={
                                                    handleVerifyOtp
                                                }
                                                disabled={
                                                    !canVerifyOtp
                                                }
                                            >
                                                {otpStatus ===
                                                    "verifying" ? (
                                                    <>
                                                        <LoaderCircle
                                                            size={
                                                                14
                                                            }
                                                            className="spin"
                                                        />

                                                        Checking
                                                    </>
                                                ) : otpStatus ===
                                                    "verified" ? (
                                                    <>
                                                        <Check
                                                            size={
                                                                14
                                                            }
                                                        />

                                                        Verified
                                                    </>
                                                ) : (
                                                    "Verify"
                                                )}
                                            </button>
                                        </div>

                                        {otpMessage && (
                                            <div
                                                className={`change-txn-password-field-status ${otpStatus ===
                                                    "verified"
                                                    ? "verified"
                                                    : otpStatus ===
                                                        "sent"
                                                        ? "sent"
                                                        : "invalid"
                                                    }`}
                                            >
                                                {otpStatus ===
                                                    "verified" ? (
                                                    <CheckCircle2
                                                        size={
                                                            14
                                                        }
                                                    />
                                                ) : otpStatus ===
                                                    "sent" ? (
                                                    <Check
                                                        size={
                                                            14
                                                        }
                                                    />
                                                ) : (
                                                    <XCircle
                                                        size={
                                                            14
                                                        }
                                                    />
                                                )}

                                                <span>
                                                    {
                                                        otpMessage
                                                    }
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}
                        </section>

                        <section className="change-txn-password-card">
                            <div className="change-txn-password-card-head">
                                <span className="change-txn-password-card-icon">
                                    <KeyRound
                                        size={
                                            19
                                        }
                                        strokeWidth={
                                            1.9
                                        }
                                    />
                                </span>

                                <div>
                                    <strong>
                                        Create new transaction password
                                    </strong>

                                    <small>
                                        Your password must satisfy every security rule before submission.
                                    </small>
                                </div>
                            </div>

                            <div className="change-txn-password-field">
                                <label htmlFor="new-transaction-password">
                                    New Transaction Password
                                </label>

                                <div className="change-txn-password-input-wrap">
                                    <LockKeyhole
                                        size={
                                            17
                                        }
                                        strokeWidth={
                                            1.8
                                        }
                                    />

                                    <input
                                        id="new-transaction-password"
                                        type={
                                            visibility.newTransactionPassword
                                                ? "text"
                                                : "password"
                                        }
                                        value={
                                            newTransactionPassword
                                        }
                                        onChange={(
                                            event
                                        ) => {
                                            setNewTransactionPassword(
                                                event.target.value
                                                    .replace(
                                                        /\s/g,
                                                        ""
                                                    )
                                                    .slice(
                                                        0,
                                                        15
                                                    )
                                            );

                                            setSuccess(
                                                false
                                            );
                                        }}
                                        placeholder="Enter your new transaction password"
                                        autoComplete="new-password"
                                        maxLength={
                                            15
                                        }
                                    />

                                    <button
                                        type="button"
                                        className="change-txn-password-eye"
                                        onClick={() =>
                                            toggleVisibility(
                                                "newTransactionPassword"
                                            )
                                        }
                                        aria-label={
                                            visibility.newTransactionPassword
                                                ? "Hide new transaction password"
                                                : "Show new transaction password"
                                        }
                                    >
                                        {visibility.newTransactionPassword ? (
                                            <EyeOff
                                                size={
                                                    18
                                                }
                                            />
                                        ) : (
                                            <Eye
                                                size={
                                                    18
                                                }
                                            />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="change-txn-password-rules">
                                {passwordRules.map(
                                    (
                                        rule
                                    ) => (
                                        <div
                                            className={`change-txn-password-rule ${rule.valid
                                                ? "valid"
                                                : "invalid"
                                                }`}
                                            key={
                                                rule.id
                                            }
                                        >
                                            <span className="change-txn-password-rule-icon">
                                                {rule.valid ? (
                                                    <Check
                                                        size={
                                                            12
                                                        }
                                                        strokeWidth={
                                                            3
                                                        }
                                                    />
                                                ) : (
                                                    <X
                                                        size={
                                                            12
                                                        }
                                                        strokeWidth={
                                                            2.4
                                                        }
                                                    />
                                                )}
                                            </span>

                                            <span>
                                                {
                                                    rule.label
                                                }
                                            </span>
                                        </div>
                                    )
                                )}
                            </div>

                            <div className="change-txn-password-field">
                                <label htmlFor="confirm-transaction-password">
                                    Re-enter New Transaction Password
                                </label>

                                <div className="change-txn-password-input-wrap">
                                    <LockKeyhole
                                        size={
                                            17
                                        }
                                        strokeWidth={
                                            1.8
                                        }
                                    />

                                    <input
                                        id="confirm-transaction-password"
                                        type={
                                            visibility.confirmTransactionPassword
                                                ? "text"
                                                : "password"
                                        }
                                        value={
                                            confirmTransactionPassword
                                        }
                                        onChange={(
                                            event
                                        ) =>
                                            setConfirmTransactionPassword(
                                                event.target.value
                                                    .replace(
                                                        /\s/g,
                                                        ""
                                                    )
                                                    .slice(
                                                        0,
                                                        15
                                                    )
                                            )
                                        }
                                        placeholder="Re-enter your new transaction password"
                                        autoComplete="new-password"
                                        maxLength={
                                            15
                                        }
                                    />

                                    <button
                                        type="button"
                                        className="change-txn-password-eye"
                                        onClick={() =>
                                            toggleVisibility(
                                                "confirmTransactionPassword"
                                            )
                                        }
                                        aria-label={
                                            visibility.confirmTransactionPassword
                                                ? "Hide confirm transaction password"
                                                : "Show confirm transaction password"
                                        }
                                    >
                                        {visibility.confirmTransactionPassword ? (
                                            <EyeOff
                                                size={
                                                    18
                                                }
                                            />
                                        ) : (
                                            <Eye
                                                size={
                                                    18
                                                }
                                            />
                                        )}
                                    </button>
                                </div>

                                {passwordMatchStatus !==
                                    "idle" && (
                                        <div
                                            className={`change-txn-password-match-status ${passwordMatchStatus}`}
                                        >
                                            {passwordMatchStatus ===
                                                "matched" ? (
                                                <CheckCircle2
                                                    size={
                                                        14
                                                    }
                                                />
                                            ) : (
                                                <XCircle
                                                    size={
                                                        14
                                                    }
                                                />
                                            )}

                                            <span>
                                                {passwordMatchStatus ===
                                                    "matched"
                                                    ? "Password matched"
                                                    : "Password not matched"}
                                            </span>
                                        </div>
                                    )}
                            </div>
                        </section>

                        <div className="change-txn-password-submit-area">
                            <div className="change-txn-password-submit-note">
                                <ShieldCheck
                                    size={
                                        15
                                    }
                                    strokeWidth={
                                        2
                                    }
                                />

                                <span>
                                    OTP verification and all password rules must be completed before updating.
                                </span>
                            </div>

                            <button
                                type="submit"
                                className="change-txn-password-submit-button"
                                disabled={
                                    !canSubmit
                                }
                            >
                                {submitting ? (
                                    <>
                                        <LoaderCircle
                                            size={
                                                16
                                            }
                                            className="spin"
                                        />

                                        Updating...
                                    </>
                                ) : (
                                    <>
                                        <KeyRound
                                            size={
                                                16
                                            }
                                            strokeWidth={
                                                2
                                            }
                                        />

                                        Update Transaction Password
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </section>
        );
    };

export default ChangeTransactionPasswordPage;
